import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import {
  FileEdit,
  AlertTriangle,
  BarChart3,
  Check,
  FileText,
  RefreshCw,
  Download,
  FolderOpen,
  Pencil
} from 'lucide-react';
import Modal from './Modal';
import NotesSection from './NotesSection';
import { useAuth } from '../contexts/AuthContext';
import {
  LeadDto,
  CreateLeadDto,
  UpdateLeadDto,
  LeadActivityDto,
  leadsApi,
  validatePhone,
  validateEmail,
  formatDate,
  BenefitType,
  getBenefitTypeOptions,
  getBenefitTypeName
} from '../services/leadsService';
import { handleApiError } from '../utils/errorHandler';
import {
  StatusDto,
  DepartmentDto,
  statusesApi,
  departmentsApi
} from '../services/settingsService';
import { contractApi, ContractDto, getContractStatusText, getDocumentTypeText } from '../services/contractService';
import InitiateSigningModal from './InitiateSigningModal';
import ContactsEditor, { ContactEntry, convertToContactEntries, convertToUpdateContactDtos, convertToCreateContactDtos, syncEmailWithPrimaryContact } from './ContactsEditor';
import AgentOrgAssignmentSection, { AgentOrgValue } from './common/AgentOrgAssignmentSection';

interface LeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (lead: LeadDto) => void;
  lead?: LeadDto | null;
  mode: 'create' | 'edit' | 'view';
  /**
   * מוטמע בעמוד (טאב "פרטי ליד" בעמוד ניהול הליד) במקום מודאל: אותו תוכן, עם סרגל כפתורים עליון.
   * צפייה ⇄ עריכה בתוך העמוד; "ביטול" בעריכה מחזיר לצפייה עם הערכים השמורים.
   */
  embedded?: boolean;
  /** נקרא כשהחוזה עובר למצב "נחתם במלואו" (חתימה ידנית או דיגיטלית) — הליד הפך ללקוח */
  onContractSigned?: () => void;
}

const EmbeddedContainer = styled.div`
  max-width: 900px;
  padding: 24px;

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const EmbeddedToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const isContractFullySigned = (contract: ContractDto | null | undefined) =>
  contract?.status?.toLowerCase() === 'fullysigned';

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
  
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${props => props.fullWidth && 'grid-column: 1 / -1;'}
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;

const RequiredLabel = styled(Label)`
  &::after {
    content: ' *';
    color: var(--error-color);
  }
`;

const Input = styled.input<{ hasError?: boolean; readOnly?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : (props.readOnly ? 'var(--border-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  transition: border-color 0.2s ease;
  background: ${props => props.readOnly ? 'var(--background-color)' : 'white'};
  color: ${props => props.readOnly ? 'var(--text-secondary)' : 'var(--text-primary)'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.readOnly ? 'var(--border-color)' : (props.hasError ? 'var(--error-color)' : 'var(--primary-color)')};
    box-shadow: ${props => props.readOnly ? 'none' : `0 0 0 2px ${props.hasError ? 'var(--error-light)' : 'var(--primary-light)'}`};
  }
`;

const Select = styled.select<{ hasError?: boolean; readOnly?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  background: ${props => props.readOnly ? 'var(--background-color)' : 'white'};
  color: ${props => props.readOnly ? 'var(--text-secondary)' : 'var(--text-primary)'};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.readOnly ? 'var(--border-color)' : (props.hasError ? 'var(--error-color)' : 'var(--primary-color)')};
    box-shadow: ${props => props.readOnly ? 'none' : `0 0 0 2px ${props.hasError ? 'var(--error-light)' : 'var(--primary-light)'}`};
  }
`;

const Textarea = styled.textarea<{ hasError?: boolean; readOnly?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  background: ${props => props.readOnly ? 'var(--background-color)' : 'white'};
  color: ${props => props.readOnly ? 'var(--text-secondary)' : 'var(--text-primary)'};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.readOnly ? 'var(--border-color)' : (props.hasError ? 'var(--error-color)' : 'var(--primary-color)')};
    box-shadow: ${props => props.readOnly ? 'none' : `0 0 0 2px ${props.hasError ? 'var(--error-light)' : 'var(--primary-light)'}`};
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--error-color);
  margin-top: 4px;
`;

const SuccessText = styled.span`
  font-size: 12px;
  color: var(--success-color);
  margin-top: 4px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover:not(:disabled) {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover:not(:disabled) { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover:not(:disabled) { background: var(--hover-background); }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  
  @media (max-width: 767px) {
    flex-direction: column-reverse;
    
    & > * {
      width: 100%;
    }
  }
`;

const DuplicateWarning = styled.div`
  background: var(--warning-light);
  color: var(--warning-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid var(--warning-color);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const WarningIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const ButtonContent = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
`;

const RefreshButton = styled.button`
  background: transparent;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
  margin-right: 8px;
  border-radius: 4px;

  &:hover {
    background: rgba(0, 142, 162, 0.1);
  }
`;

const ActivitiesSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
`;

const ActivitiesTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
`;

const ActivityItem = styled.div`
  padding: 12px;
  background: var(--background-color);
  border-radius: var(--border-radius-sm);
  margin-bottom: 8px;
  border-right: 3px solid var(--primary-color);
`;

const ActivityMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  font-size: 12px;
  color: var(--text-secondary);
`;

const ActivityDescription = styled.div`
  font-size: 14px;
  color: var(--text-primary);
`;

const LoadingText = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
`;

const CurrentStatusInfo = styled.div`
  background: var(--primary-light);
  color: var(--primary-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid var(--primary-color);
  font-size: 14px;
  font-weight: 500;
`;

const StatusInfoMessage = styled.div`
  background: var(--background-color);
  color: var(--text-secondary);
  padding: 8px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  margin-top: 4px;
  border-right: 3px solid var(--primary-color);
`;

const LeadModal: React.FC<LeadModalProps> = ({
  isOpen,
  onClose,
  onSave,
  lead,
  mode,
  embedded = false,
  onContractSigned
}) => {
  const { user, isManager, departmentIds, canViewClientFinance } = useAuth();
  // מוטמע: "ביטול" בעריכה מאתחל מחדש את הטופס מהליד השמור (מריץ שוב את אפקט האתחול)
  const [resetKey, setResetKey] = useState(0);

  // המצב הפעיל בפועל. ה-prop mode הוא רק המצב ההתחלתי; ניתן לעבור מ-view ל-edit
  // בלחיצה על כפתור העריכה בתוך המודל.
  const [activeMode, setActiveMode] = useState<'create' | 'edit' | 'view'>(mode);

  const [formData, setFormData] = useState<CreateLeadDto | UpdateLeadDto>({
    fullName: '',
    email: '',
    notes: '',
    benefitType: 0,
    otherBenefitDescription: '',
    contacts: [],
    // שדות משותפים לכל החוזים
    clientAddress: '',
    clientCity: '',
    clientIdNumber: '',
    isClientForeignResident: false,
    // שדות ספציפיים לחוזה ילדים
    fatherName: '',
    fatherIdNumber: '',
    isFatherForeignResident: false,
    motherName: '',
    motherIdNumber: '',
    isMotherForeignResident: false,
    fatherAddress: '',
    fatherCity: '',
    motherAddress: '',
    motherCity: '',
    ...((mode === 'edit' || mode === 'view') && { statusId: 0 })
  });

  // אנשי קשר (טלפון + אימייל אופציונלי)
  const [contactEntries, setContactEntries] = useState<ContactEntry[]>([
    { id: 0, number: '', email: '', label: 'ראשי', isPrimary: true, sortOrder: 0 }
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<LeadDto | null>(null);
  const [activities, setActivities] = useState<LeadActivityDto[]>([]);
  const [statuses, setStatuses] = useState<StatusDto[]>([]);
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  // מחלקה מנוהלת בנפרד מ-formData: ביצירה נשלחת ב-CreateLeadDto; בעריכה נשמרת דרך endpoint ייעודי (Manager בלבד)
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  // P8b: שיוך סוכן/ארגון מפנה + 4 ערכי עמלה — נשלחים ב-Create/Update DTO (השיוך לכל הצוות; העמלות מגודרות בשרת)
  const [agentOrg, setAgentOrg] = useState<AgentOrgValue>({});
  const [contract, setContract] = useState<ContractDto | null>(null);
  const [showSigningModal, setShowSigningModal] = useState(false);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [allStatusesData, departmentsData] = await Promise.all([
        statusesApi.search('', 1), // Only load lead statuses (group 1)
        departmentsApi.getAll()
      ]);

      // Always load all lead statuses so switching view→edit shows the full list immediately
      setStatuses(allStatusesData);
      setDepartments(departmentsData);
    } catch (err) {
      toast.error(handleApiError(err, 'שגיאה בטעינת נתוני הליד'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadActivities = async (leadId: number) => {
    try {
      const activitiesData = await leadsApi.getActivities(leadId);
      setActivities(activitiesData);
    } catch (err) {
      toast.error(handleApiError(err, 'שגיאה בטעינת הפעילויות'));
    }
  };

  const loadContract = useCallback(async (leadId: number): Promise<ContractDto | null> => {
    try {
      const contractData = await contractApi.getByLeadId(leadId);
      setContract(contractData);
      return contractData;
    } catch (err) {
      console.error('Error loading contract:', err);
      setContract(null);
      return null;
    }
  }, []);

  // רענון החוזה (פולינג / כפתור רענון) — מעבר ל"נחתם במלואו" מדווח פעם אחת להורה
  const onContractSignedRef = useRef(onContractSigned);
  onContractSignedRef.current = onContractSigned;
  const refreshContract = useCallback(async (leadId: number, wasFullySigned: boolean) => {
    const refreshed = await loadContract(leadId);
    if (!wasFullySigned && isContractFullySigned(refreshed)) {
      onContractSignedRef.current?.();
    }
  }, [loadContract]);

  useEffect(() => {
    if (isOpen) {
      // איפוס המצב הפעיל למצב ההתחלתי בכל פתיחה של המודל
      setActiveMode(mode);
      loadInitialData();

      if (lead && (mode === 'edit' || mode === 'view')) {
        setFormData({
          fullName: lead.fullName,
          email: lead.email || '',
          contacts: [],
          notes: lead.notes || '',
          statusId: lead.statusId,
          benefitType: lead.benefitType,
          otherBenefitDescription: lead.otherBenefitDescription || '',
          // שדות משותפים
          clientAddress: lead.clientAddress || '',
          clientCity: lead.clientCity || '',
          clientIdNumber: lead.clientIdNumber || '',
          isClientForeignResident: lead.isClientForeignResident ?? false,
          // שדות חוזה ילדים
          fatherName: lead.fatherName || '',
          fatherIdNumber: lead.fatherIdNumber || '',
          isFatherForeignResident: lead.isFatherForeignResident ?? false,
          motherName: lead.motherName || '',
          motherIdNumber: lead.motherIdNumber || '',
          isMotherForeignResident: lead.isMotherForeignResident ?? false,
          fatherAddress: lead.fatherAddress || '',
          fatherCity: lead.fatherCity || '',
          motherAddress: lead.motherAddress || '',
          motherCity: lead.motherCity || '',
        });

        // מחלקה — נטענת מהליד הקיים (עריכה ניתנת ל-Manager בלבד; עובד רואה קריאה-בלבד)
        setDepartmentId(lead.departmentId ?? null);

        // P8b: שיוך סוכן/ארגון + ערכי עמלה מהליד (ערכי העמלה יגיעו null אם למשתמש אין CanViewClientFinance)
        setAgentOrg({
          agentSourceId: lead.agentSourceId ?? null,
          organizationSourceId: lead.organizationSourceId ?? null,
          agentSigningCommissionAmount: lead.agentSigningCommissionAmount ?? null,
          agentApprovalCommissionPercentage: lead.agentApprovalCommissionPercentage ?? null,
          organizationSigningCommissionAmount: lead.organizationSigningCommissionAmount ?? null,
          organizationApprovalCommissionPercentage: lead.organizationApprovalCommissionPercentage ?? null,
        });

        // טעינת אנשי קשר
        setContactEntries(convertToContactEntries(lead.contacts));

        loadActivities(lead.id);
        loadContract(lead.id);
      } else {
        setFormData({
          fullName: '',
          email: '',
          contacts: [],
          notes: '',
          benefitType: 0,
          otherBenefitDescription: '',
          // שדות משותפים
          clientAddress: '',
          clientCity: '',
          clientIdNumber: '',
          isClientForeignResident: false,
          // שדות חוזה ילדים
          fatherName: '',
          fatherIdNumber: '',
          isFatherForeignResident: false,
          motherName: '',
          motherIdNumber: '',
          isMotherForeignResident: false,
          fatherAddress: '',
          fatherCity: '',
          motherAddress: '',
          motherCity: '',
        });
        // ברירת-מחדל למחלקה ביצירה = מחלקת המשתמש היוצר (אם משויך). ניתן לשינוי/לניקוי
        setDepartmentId(departmentIds && departmentIds.length > 0 ? departmentIds[0] : null);
        setAgentOrg({}); // P8b: ללא שיוך סוכן/ארגון ביצירה חדשה
        setContactEntries([
          { id: 0, number: '', email: '', label: 'ראשי', isPrimary: true, sortOrder: 0 }
        ]);
        setActivities([]);
        setContract(null);
      }
      
      setErrors({});
      setDuplicateWarning(null);
    }
  }, [isOpen, lead, mode, loadInitialData, loadContract, departmentIds, resetKey]);

  // Periodic contract status refresh for view mode
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isOpen && lead && activeMode === 'view' && contract) {
      const status = contract.status?.toLowerCase();
      if (status === 'sent' || status === 'viewed' || status === 'partialsigned') {
        // Refresh contract status every 30 seconds for sent contracts
        intervalId = setInterval(() => {
          refreshContract(lead.id, false);
        }, 30000);
      }
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isOpen, lead, activeMode, contract?.status, contract, refreshContract]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName?.trim()) {
      newErrors.fullName = 'שם מלא הוא שדה חובה';
    }

    // וידוא שיש לפחות איש קשר אחד עם טלפון ראשי
    const primaryContact = contactEntries.find(p => p.isPrimary);
    if (!primaryContact || !primaryContact.number?.trim()) {
      newErrors.phone = 'טלפון ראשי הוא שדה חובה';
    } else if (!validatePhone(primaryContact.number)) {
      newErrors.phone = 'מספר טלפון ראשי לא תקין';
    }

    // וידוא שכל הטלפונים תקינים
    const invalidPhones = contactEntries.filter(p => p.number.trim() && !validatePhone(p.number));
    if (invalidPhones.length > 0) {
      newErrors.contacts = 'יש מספרי טלפון לא תקינים';
    }

    // וידוא שכל האימיילים תקינים (אם הוזנו)
    const invalidEmails = contactEntries.filter(p => p.email.trim() && !validateEmail(p.email));
    if (invalidEmails.length > 0) {
      newErrors.contacts = 'יש כתובת דוא"ל לא תקינה';
    }

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'כתובת דוא"ל לא תקינה';
    }

    if (activeMode === 'edit' && (!('statusId' in formData) || formData.statusId === 0)) {
      newErrors.statusId = 'יש לבחור סטטוס';
    }

    // Validate benefit type (required field)
    if (!formData.benefitType || formData.benefitType === 0) {
      newErrors.benefitType = 'יש לבחור סוג קצבה';
    }

    // Validate otherBenefitDescription when benefitType is Other
    if (formData.benefitType === BenefitType.Other && !formData.otherBenefitDescription?.trim()) {
      newErrors.otherBenefitDescription = 'נא לפרט את סוג הקצבה';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicate = async (phone: string) => {
    try {
      const duplicate = await leadsApi.checkDuplicate(phone);
      if (duplicate && (mode === 'create' || duplicate.id !== lead?.id)) {
        setDuplicateWarning(duplicate);
      } else {
        setDuplicateWarning(null);
      }
    } catch (err) {
      console.error('Error checking duplicate:', err);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      let savedLead: LeadDto;

      if (activeMode === 'edit' && lead) {
        const updateData: UpdateLeadDto = {
          ...(formData as UpdateLeadDto),
          ...agentOrg, // P8b: שיוך סוכן/ארגון + 4 ערכי עמלה (השרת מגדר את ערכי העמלה לפי CanViewClientFinance)
          contacts: convertToUpdateContactDtos(contactEntries)
        };
        savedLead = await leadsApi.update(lead.id, updateData);

        // שינוי מחלקה אחרי יצירה — Manager בלבד, דרך endpoint ייעודי (departmentId לא נכלל ב-UpdateLeadDto)
        if (isManager && (departmentId ?? null) !== (lead.departmentId ?? null)) {
          savedLead = await leadsApi.setDepartment(lead.id, departmentId);
        }
      } else if (activeMode === 'create') {
        const createData: CreateLeadDto = {
          ...(formData as CreateLeadDto),
          departmentId: departmentId ?? undefined, // שיוך-מחלקה ביצירה (כל הצוות)
          ...agentOrg, // P8b: שיוך סוכן/ארגון + 4 ערכי עמלה (כל הצוות ממלא ביצירה)
          contacts: convertToCreateContactDtos(contactEntries)
        };
        savedLead = await leadsApi.create(createData);
      } else {
        // View mode - no save operation
        return;
      }

      onSave(savedLead);
      if (embedded) {
        // בעמוד — חוזרים לצפייה; ההורה טוען מחדש את הליד
        setActiveMode('view');
      } else {
        onClose();
      }
    } catch (err) {
      toast.error(handleApiError(err, activeMode === 'edit' ? 'שגיאה בעדכון הליד' : 'שגיאה ביצירת הליד'));
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNotesChange = (updatedActivities: LeadActivityDto[]) => {
    setActivities(updatedActivities);
  };

  const getModalTitle = (): string => {
    switch (activeMode) {
      case 'create':
        return 'הוספת ליד חדש';
      case 'edit':
        return 'עריכת ליד';
      case 'view':
        return 'צפייה בליד';
      default:
        return 'ליד';
    }
  };

  const getCurrentStatusName = (): string => {
    if (!lead?.statusId) return '';
    const currentStatus = statuses.find(s => s.id === lead.statusId);
    return currentStatus?.name || '';
  };

  const handleDownloadDocument = async (documentId: number, documentType: string) => {
    try {
      setLoading(true);
      const blob = await contractApi.downloadDocumentPdf(documentId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${getDocumentTypeText(documentType)}_${contract?.contractNumber || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בהורדת המסמך'));
    } finally {
      setLoading(false);
    }
  };

  const isReadOnly = activeMode === 'view';
  const shouldShowSaveButton = activeMode !== 'view';
  // מחלקה: ניתנת לעריכה לכל הצוות ביצירה; בעריכה — Manager בלבד; בצפייה — קריאה-בלבד
  const canEditDepartment = activeMode === 'create' || (activeMode === 'edit' && isManager);
  // ליד שכבר חתם והפך ללקוח — אין החתמה נוספת
  const canInitiateSigning = !!lead && !lead.clientId;

  // מוטמע: "ביטול" בעריכה חוזר לצפייה עם הערכים השמורים; במודאל — סגירה
  const handleCancel = () => {
    if (embedded) {
      setResetKey(prev => prev + 1);
    } else {
      onClose();
    }
  };

  const actionButtons = (
    <ButtonGroup>
      {!(embedded && activeMode === 'view') && (
        <Button onClick={handleCancel} disabled={saving}>
          {activeMode === 'view' ? 'סגירה' : 'ביטול'}
        </Button>
      )}
      {activeMode === 'view' && lead && (
        <Button
          onClick={() => setActiveMode('edit')}
          disabled={saving || loading}
        >
          <ButtonContent><Pencil size={16} strokeWidth={2} />עריכה</ButtonContent>
        </Button>
      )}
      {(activeMode === 'edit' || activeMode === 'view') && canInitiateSigning && (
        <Button
          variant="primary"
          onClick={() => setShowSigningModal(true)}
          disabled={saving || loading}
        >
          <ButtonContent><FileEdit size={16} strokeWidth={2} />חתום לקוח</ButtonContent>
        </Button>
      )}
      {shouldShowSaveButton && (
        <Button variant="primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? 'שומר...' : 'שמירה'}
        </Button>
      )}
    </ButtonGroup>
  );

  const content = (
    <>
      {loading ? (
        <LoadingText>טוען נתונים...</LoadingText>
      ) : (
        <>
          {duplicateWarning && (
            <DuplicateWarning>
              <WarningIcon><AlertTriangle size={18} strokeWidth={2} /></WarningIcon>
              נמצא ליד קיים עם אותו מספר טלפון: {duplicateWarning.fullName} (נוצר ב{formatDate(duplicateWarning.createdAt)})
            </DuplicateWarning>
          )}

          {activeMode === 'view' && lead && (
            <CurrentStatusInfo>
              <StatusIcon><BarChart3 size={16} strokeWidth={2} /></StatusIcon> סטטוס נוכחי: {getCurrentStatusName()}
            </CurrentStatusInfo>
          )}

          {contract && (
            <CurrentStatusInfo style={{
              backgroundColor: contract.status?.toLowerCase() === 'fullysigned'
                ? '#d1fae5' // ירוק בהיר לחתום במלואו
                : 'var(--primary-light)',
              borderColor: contract.status?.toLowerCase() === 'fullysigned'
                ? '#10b981' // ירוק כהה
                : 'var(--primary-color)'
            }}>
              {contract.status?.toLowerCase() === 'fullysigned' && <StatusIcon><Check size={16} strokeWidth={2} color="#10b981" /></StatusIcon>}
              <StatusIcon><FileText size={16} strokeWidth={2} /></StatusIcon> חוזה: {contract.contractNumber} - סטטוס: {getContractStatusText(contract.status)}
              {activeMode === 'view' && (
                <RefreshButton
                  onClick={() => lead && refreshContract(lead.id, isContractFullySigned(contract))}
                  title="רענן סטטוס חוזה"
                >
                  <RefreshCw size={14} strokeWidth={2} />
                </RefreshButton>
              )}
              {contract.documents && contract.documents.filter(doc => doc.status?.toLowerCase() !== 'signed').length > 0 &&
              contract.expiresAt && new Date(contract.expiresAt) > new Date() && (
                <StatusInfoMessage>
                  תוקף החתימה עד: {new Date(contract.expiresAt).toLocaleDateString('he-IL')}
                </StatusInfoMessage>
              )}

              {/* Download signed documents */}
              {contract.documents && contract.documents.filter(doc => doc.status?.toLowerCase() === 'signed').length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <strong><StatusIcon><Download size={14} strokeWidth={2} /></StatusIcon> הורדת מסמכים חתומים:</strong>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
                    {contract.documents
                      .filter(doc => doc.status?.toLowerCase() === 'signed')
                      .map(doc => (
                        <Button
                          key={doc.id}
                          variant="secondary"
                          onClick={() => handleDownloadDocument(doc.id, doc.documentType)}
                          disabled={loading}
                          style={{ 
                            fontSize: '12px', 
                            padding: '6px 12px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none'
                          }}
                        >
                          <ButtonContent><FolderOpen size={14} strokeWidth={2} />{getDocumentTypeText(doc.documentType)}</ButtonContent>
                        </Button>
                      ))}
                  </div>
                </div>
              )}
            </CurrentStatusInfo>
          )}

          <FormGrid>
            <FormField>
              <RequiredLabel>
                {formData.benefitType === BenefitType.DisabledChildPhysical ||
                 formData.benefitType === BenefitType.DisabledChildMental
                  ? "שם הקטין"
                  : formData.benefitType === BenefitType.NursingCare
                  ? "שם המבוטח"
                  : "שם הלקוח"}
              </RequiredLabel>
              <Input
                type="text"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                hasError={!!errors.fullName}
                placeholder="הזן שם מלא"
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
              {errors.fullName && <ErrorText>{errors.fullName}</ErrorText>}
            </FormField>

            <FormField fullWidth>
              <RequiredLabel>אנשי קשר</RequiredLabel>
              <ContactsEditor
                contacts={contactEntries}
                onChange={(newEntries) => {
                  // מייל איש הקשר הראשי מסתנכרן לשדה הדוא"ל של הליד
                  const currentEmail = formData.email ?? '';
                  const syncedEmail = syncEmailWithPrimaryContact(contactEntries, newEntries, currentEmail);
                  if (syncedEmail !== currentEmail) {
                    setFormData(prev => ({ ...prev, email: syncedEmail }));
                    if (errors.email) {
                      setErrors(prev => ({ ...prev, email: '' }));
                    }
                  }
                  setContactEntries(newEntries);
                  // בדיקת כפילות עבור הטלפון הראשי
                  const primary = newEntries.find(p => p.isPrimary);
                  if (primary && primary.number.trim() && validatePhone(primary.number)) {
                    checkDuplicate(primary.number.trim());
                  } else {
                    setDuplicateWarning(null);
                  }
                  if (errors.phone) {
                    setErrors(prev => ({ ...prev, phone: '' }));
                  }
                  if (errors.contacts) {
                    setErrors(prev => ({ ...prev, contacts: '' }));
                  }
                }}
                disabled={isReadOnly}
              />
              {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
              {errors.contacts && <ErrorText>{errors.contacts}</ErrorText>}
            </FormField>

            <FormField>
              <Label>דוא"ל ראשי של הליד</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                hasError={!!errors.email}
                placeholder="example@email.com"
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
              {errors.email && <ErrorText>{errors.email}</ErrorText>}
              {formData.email && validateEmail(formData.email) && (
                <SuccessText>✓ כתובת דוא"ל תקינה</SuccessText>
              )}
            </FormField>

            <FormField>
              <RequiredLabel>סוג קצבה</RequiredLabel>
              {activeMode === 'view' ? (
                <Input
                  type="text"
                  value={formData.benefitType ? `${getBenefitTypeName(formData.benefitType)}${formData.benefitType === BenefitType.Other && formData.otherBenefitDescription ? ` - ${formData.otherBenefitDescription}` : ''}` : ''}
                  readOnly
                  disabled
                />
              ) : (
                <Select
                  value={formData.benefitType || 0}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    handleInputChange('benefitType', newValue);
                    // Clear otherBenefitDescription if not "Other"
                    if (newValue !== BenefitType.Other) {
                      handleInputChange('otherBenefitDescription', '');
                    }
                  }}
                  hasError={!!errors.benefitType}
                  readOnly={isReadOnly}
                  disabled={isReadOnly}
                >
                  <option value={0}>בחר סוג קצבה</option>
                  {getBenefitTypeOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              )}
              {errors.benefitType && <ErrorText>{errors.benefitType}</ErrorText>}
            </FormField>

            {formData.benefitType === BenefitType.Other && activeMode !== 'view' && (
              <FormField>
                <RequiredLabel>פירוט סוג קצבה</RequiredLabel>
                <Input
                  type="text"
                  value={formData.otherBenefitDescription || ''}
                  onChange={(e) => handleInputChange('otherBenefitDescription', e.target.value)}
                  hasError={!!errors.otherBenefitDescription}
                  placeholder="אנא פרט את סוג הקצבה"
                  readOnly={isReadOnly}
                  disabled={isReadOnly}
                />
                {errors.otherBenefitDescription && <ErrorText>{errors.otherBenefitDescription}</ErrorText>}
              </FormField>
            )}

            <FormField>
              <Label>מחלקה</Label>
              <Select
                value={departmentId ?? ''}
                onChange={(e) => setDepartmentId(e.target.value ? parseInt(e.target.value) : null)}
                readOnly={!canEditDepartment}
                disabled={!canEditDepartment}
              >
                <option value="">ללא מחלקה</option>
                {departments.filter(d => d.isActive || d.id === departmentId).map(dep => (
                  <option key={dep.id} value={dep.id}>
                    {dep.name}
                  </option>
                ))}
              </Select>
            </FormField>

            {/* P8b: שיוך סוכן/ארגון מפנה + עמלות פר-תיק. השיוך לכל הצוות; ערכי העמלה — ביצירה לכל הצוות, בעריכה רק ל-CanViewClientFinance */}
            <AgentOrgAssignmentSection
              value={agentOrg}
              onChange={(patch) => setAgentOrg(prev => ({ ...prev, ...patch }))}
              showCommissions={activeMode === 'create' ? true : canViewClientFinance}
              disabled={activeMode === 'view'}
            />

            {activeMode === 'edit' && (
              <FormField>
                <RequiredLabel>סטטוס</RequiredLabel>
                <Select
                  value={('statusId' in formData) ? formData.statusId : 0}
                  onChange={(e) => handleInputChange('statusId', parseInt(e.target.value))}
                  hasError={!!errors.statusId}
                >
                  <option value={0}>בחר סטטוס</option>
                  {statuses.map(status => (
                    <option key={status.id} value={status.id}>
                      {status.name} {status.id === lead?.statusId ? '(נוכחי)' : ''}
                    </option>
                  ))}
                </Select>
                {errors.statusId && <ErrorText>{errors.statusId}</ErrorText>}
                {lead?.statusId && statuses.length > 1 && (
                  <StatusInfoMessage>
                    ✓ מציג את כל הסטטוסים הזמינים של לידים
                  </StatusInfoMessage>
                )}
              </FormField>
            )}

            <FormField fullWidth>
              <Label>הערות</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="הערות נוספות על הליד..."
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
            </FormField>
          </FormGrid>

          {activeMode === 'edit' && (
            <>
              <NotesSection
                leadId={lead?.id || 0}
                activities={activities}
                onNotesChange={handleNotesChange}
                currentUserId={user?.id || undefined}
                isSystemAdmin={isManager}
              />

              {isManager && (
                <>
                  {activities.filter(a => a.activityType !== 6).length > 0 && (
                    <ActivitiesSection>
                      <ActivitiesTitle>היסטוריית פעילות</ActivitiesTitle>
                      {activities
                        .filter(activity => activity.activityType !== 6) // Exclude notes
                        .map(activity => (
                          <ActivityItem key={activity.id}>
                            <ActivityMeta>
                              <span>{activity.activityTypeName}</span>
                              <span>{activity.performedByUserName} • {formatDate(activity.createdAt)}</span>
                            </ActivityMeta>
                            <ActivityDescription>{activity.description}</ActivityDescription>
                          </ActivityItem>
                        ))}
                    </ActivitiesSection>
                  )}
                </>
              )}
            </>
          )}
          
          {activeMode === 'view' && (
            <>
              {/* Show only existing notes as read-only in view mode */}
              {activities.filter(a => a.activityType === 6).length > 0 && (
                <ActivitiesSection>
                  <ActivitiesTitle>הערות</ActivitiesTitle>
                  {activities
                    .filter(activity => activity.activityType === 6) // Only notes
                    .map(activity => (
                      <ActivityItem key={activity.id}>
                        <ActivityMeta>
                          <span>{activity.performedByUserName} • {formatDate(activity.createdAt)}</span>
                        </ActivityMeta>
                        <ActivityDescription>{activity.description}</ActivityDescription>
                      </ActivityItem>
                    ))}
                </ActivitiesSection>
              )}
              
              {isManager && (
                <>
                  {activities.filter(a => a.activityType !== 6).length > 0 && (
                    <ActivitiesSection>
                      <ActivitiesTitle>היסטוריית פעילות</ActivitiesTitle>
                      {activities
                        .filter(activity => activity.activityType !== 6) // Exclude notes
                        .map(activity => (
                          <ActivityItem key={activity.id}>
                            <ActivityMeta>
                              <span>{activity.activityTypeName}</span>
                              <span>{activity.performedByUserName} • {formatDate(activity.createdAt)}</span>
                            </ActivityMeta>
                            <ActivityDescription>{activity.description}</ActivityDescription>
                          </ActivityItem>
                        ))}
                    </ActivitiesSection>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
      
      {lead && (
        <InitiateSigningModal
          isOpen={showSigningModal}
          onClose={() => setShowSigningModal(false)}
          onSuccess={(newContract) => {
            setContract(newContract);
            setShowSigningModal(false);
            // חתימה ידנית — החוזה נחתם במלואו והלקוח נוצר מיד
            if (isContractFullySigned(newContract)) {
              onContractSigned?.();
            }
          }}
          lead={lead}
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <EmbeddedContainer>
        <EmbeddedToolbar>{actionButtons}</EmbeddedToolbar>
        {content}
      </EmbeddedContainer>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      width="700px"
      maxWidth="95vw"
      footer={actionButtons}
    >
      {content}
    </Modal>
  );
};

export default LeadModal;